import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import Header from "../../../../components/common/header";
import { track } from "@amplitude/analytics-browser";
import { gql } from "../../../../__generated__";
import { FaBell } from "react-icons/fa";

// 알림 설정 관련 쿼리들
export const GET_NOTIFICATION_SETTINGS = gql(`
  query getNotificationSettings {
    getNotificationSettings {
      id
      userId
      isEnabled
      notificationType {
        id
        keyName
        displayName
        description
        iconName
        color
        sortOrder
      }
    }
  }
`);

export const GET_AVAILABLE_NOTIFICATION_TYPES = gql(`
  query getAvailableNotificationTypes {
    getAvailableNotificationTypes {
      id
      keyName
      displayName
      description
      iconName
      color
      sortOrder
      isEnabled
    }
  }
`);

export const UPDATE_NOTIFICATION_SETTING = gql(`
  mutation updateNotificationSetting($input: UpdateNotificationSettingInput!) {
    updateNotificationSetting(input: $input) {
      id
      userId
      isEnabled
      notificationType {
        id
        keyName
        displayName
        description
        iconName
        color
      }
    }
  }
`);

const NotificationSettingsPage = (): JSX.Element => {
  const navigate = useNavigate();

  // GraphQL 쿼리 및 뮤테이션
  const {
    data: settingsData,
    loading: settingsLoading,
    error: settingsError,
  } = useQuery(GET_NOTIFICATION_SETTINGS);
  const {
    data: typesData,
    loading: typesLoading,
    error: typesError,
  } = useQuery(GET_AVAILABLE_NOTIFICATION_TYPES);
  const [updateSetting] = useMutation(UPDATE_NOTIFICATION_SETTING);

  // 로컬 상태 (DB에서 가져온 데이터로 초기화)
  const [settings, setSettings] = useState<Record<string, boolean>>({});
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});

  // DB에서 데이터 로드
  useEffect(() => {
    if (
      settingsData?.getNotificationSettings &&
      typesData?.getAvailableNotificationTypes
    ) {
      const settingsMap: Record<string, boolean> = {};

      // 사용자 설정이 있는 경우
      settingsData.getNotificationSettings.forEach((setting) => {
        settingsMap[setting.notificationType.keyName] = setting.isEnabled;
      });

      // 사용자 설정이 없는 알림 타입은 기본값으로 설정
      typesData.getAvailableNotificationTypes.forEach((type) => {
        if (!(type.keyName in settingsMap)) {
          settingsMap[type.keyName] = true; // 기본값
        }
      });

      setSettings(settingsMap);
    }
  }, [settingsData, typesData]);

  const handleToggle = async (keyName: string) => {
    const newValue = !settings[keyName];

    // 로컬 상태 즉시 업데이트 (낙관적 업데이트)
    setSettings((prev) => ({
      ...prev,
      [keyName]: newValue,
    }));

    // 업데이트 중 상태 설정
    setIsUpdating((prev) => ({
      ...prev,
      [keyName]: true,
    }));

    try {
      // 해당 알림 타입의 ID 찾기
      const notificationType = typesData?.getAvailableNotificationTypes.find(
        (type) => type.keyName === keyName
      );

      if (notificationType) {
        await updateSetting({
          variables: {
            input: {
              notificationTypeId: notificationType.id,
              isEnabled: newValue,
            },
          },
        });

        track("알림 설정 토글", {
          setting: keyName,
          enabled: newValue,
        });
      }
    } catch (error) {
      // 실패 시 롤백
      setSettings((prev) => ({
        ...prev,
        [keyName]: !newValue,
      }));

      console.error("알림 설정 업데이트 실패:", error);
      // TODO: 에러 토스트 표시
    } finally {
      // 업데이트 중 상태 해제
      setIsUpdating((prev) => ({
        ...prev,
        [keyName]: false,
      }));
    }
  };

  const getIconComponent = (color: string | null | undefined) => {
    const iconClass = `text-${color}-600`;

    // 모든 알림 타입에 대해 통일된 종(bell) 아이콘 사용
    return <FaBell className={`w-4 h-4 ${iconClass}`} />;
  };

  // 로딩 상태
  if (settingsLoading || typesLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header
          title="알림설정"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
        <div className="pt-16 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">알림 설정을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (settingsError || typesError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header
          title="알림설정"
          showBackButton={true}
          onBackClick={() => navigate(-1)}
        />
        <div className="pt-16 pb-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-gray-600 mb-2">알림 설정을 불러올 수 없습니다</p>
            <button
              onClick={() => window.location.reload()}
              className="text-blue-600 hover:text-blue-800"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    );
  }

  const availableTypes = typesData?.getAvailableNotificationTypes || [];
  const sortedTypes = [...availableTypes].sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="알림설정"
        showBackButton={true}
        onBackClick={() => navigate(-1)}
      />

      <div className="pt-16 pb-20">
        <div className="px-4 space-y-4">
          {sortedTypes.map((type) => (
            <div key={type.id} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <div
                      className={`w-8 h-8 bg-${type.color}-100 rounded-full flex items-center justify-center`}
                    >
                      {getIconComponent(type.color)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">
                        {type.displayName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {type.description}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(type.keyName)}
                  disabled={isUpdating[type.keyName]}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings[type.keyName] ? "bg-blue-600" : "bg-gray-200"
                  } ${
                    isUpdating[type.keyName]
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings[type.keyName] ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
